/**
 * N8N Data Fetchers
 *
 * Wrapper functions that combine N8N service calls with data transformers
 * to provide clean, typed data for the UI layer.
 */

import { n8nService } from '../components/core/services/n8n.service';
import { backendProvider } from '../services/backend';
import { teamInviteService } from '../services/backend/appwrite/teamInviteService';
import { applyPendingIsrWritesToCard } from './isrPendingWrites';
import {
  transformCampaignMetrics,
  transformLeadCard,
  transformN8NToMappedLead,
} from './n8nTransformers';
import type { MinimalLeadData, MappedLeadData, CampaignMetadata } from '../types';
import type { TeamInvitationData, TeamMemberData } from '../types/n8nResponses';

export interface CampaignsResult {
  campaigns: CampaignMetadata[];
  teamMembers: TeamMemberData[];
  teamInvitations: TeamInvitationData[];
}

/**
 * Bundle shape of the get_leads_cards n8n response when it returns ALL
 * campaigns (its current behavior — see fetchLeadsFromN8N's WORKAROUND note).
 * Discriminated by the presence of the `campaigns` array on the envelope,
 * NEVER by array length: a tenant with one campaign still gets the
 * multi-campaign envelope, so cardinality-based detection would permanently
 * disable bundle seeding for them.
 */
export interface LeadsBundleFromN8N {
  campaigns: Array<{
    campaign_id: string;
    campaign_name?: string;
    leads: any[];
  }>;
}

/** True when the response carries the all-campaigns bundle envelope. */
export const isLeadsBundleResponse = (data: unknown): data is LeadsBundleFromN8N =>
  Boolean(
    data &&
    typeof data === 'object' &&
    Array.isArray((data as LeadsBundleFromN8N).campaigns)
  );

/**
 * Fetch all leads for a campaign (for grid view)
 * Returns transformed MinimalLeadData array sorted by ICP score (descending)
 */
export async function fetchLeadsFromN8N(
  campaignId: string,
  userEmail: string
): Promise<MinimalLeadData[]> {
  try {
    const response = await n8nService.getLeadsCards({
      campaign_id: campaignId,
      client_email: userEmail,
    });

    if (!response.success || !response.data) {
      // THROW on failure instead of returning [] — TanStack Query enters error
      // state, the UI shows a retry banner, and the existing cache is preserved
      // (placeholderData / previous data). Silently returning [] causes "No Leads
      // Yet" to flash when the n8n-proxy times out under load.
      const errorMsg = response.error || `N8N webhook failed for campaign ${campaignId}`;
      console.warn('Failed to fetch leads from N8N:', errorMsg);
      throw new Error(errorMsg);
    }

    // WORKAROUND: N8N workflow returns ALL campaigns instead of just requested one
    // Extract leads for the requested campaign from the campaigns array
    let leadsData;
    let campaignName = '';

    if (response.data.campaigns && Array.isArray(response.data.campaigns)) {
      const targetCampaign = response.data.campaigns.find(
        (c: any) => c.campaign_id === campaignId
      );

      if (!targetCampaign) {
        // Distinguish "campaign deleted" from a truncated/phantom response:
        // a user asking about campaign X should have X in their bundle — unless
        // the list is EMPTY, which means the proxy short-circuited (known
        // phantom-execution shape) and the lead list must not be wiped.
        if (response.data.campaigns.length === 0) {
          throw new Error(`N8N returned an empty campaigns bundle for ${campaignId} (possible phantom execution)`);
        }
        console.warn(`Campaign ${campaignId} not found in N8N response`);
        return [];
      }

      leadsData = targetCampaign.leads || [];
      campaignName = targetCampaign.campaign_name || '';
    } else if (response.data.leads && Array.isArray(response.data.leads)) {
      leadsData = response.data.leads;
      campaignName = response.data.campaign_name || '';
    } else {
      console.warn(`No leads array in N8N response for campaign ${campaignId}`);
      throw new Error(`No leads payload in N8N response for campaign ${campaignId}`);
    }

    // Always use the campaignId we REQUESTED, not the one from N8N response
    const parentCampaignId = campaignId;

    const leads = leadsData.map((lead: any) => {
      const card = transformLeadCard({
        ...lead,
        campaign_id: lead.campaign_id || parentCampaignId,
        campaign_name: lead.campaign_name || campaignName || '',
      });
      // Inside the n8n→DB sync window after an ISR save, the source still
      // reports the PRE-save values — overlay the saved ones so the list
      // never bounces the user's edits back.
      return applyPendingIsrWritesToCard(card, parentCampaignId);
    });

    // Sort by ICP score (descending) - default sorting
    return leads.sort((a, b) => (b.icp_score || 0) - (a.icp_score || 0));
  } catch (error) {
    console.error('Error fetching leads from N8N:', error);
    // Re-throw so TanStack Query enters error state — the UI shows a retry
    // option and keeps the previous cache rather than flashing "No Leads Yet".
    // This is critical when the n8n-proxy times out from load.
    throw error;
  }
}

/**
 * ONE get_leads_cards call, returning every campaign's transformed leads.
 *
 * The n8n workflow returns ALL campaigns regardless of the requested id (see
 * the WORKAROUND in fetchLeadsFromN8N) — so N callers previously made N
 * heavyweight calls and discarded N−1 of them. This fetcher makes the
 * over-return explicit and cheap to exploit: one call, caller seeds every
 * campaign cache from the bundle.
 *
 * Returns null when the response does NOT carry the all-campaigns envelope
 * (n8n contract change) — callers must fall back to per-campaign
 * fetchLeadsFromN8N. Shape-discriminated, never cardinality-based.
 */
/**
 * In-flight + short-TTL memo for the bundle fetch. Several callers race on
 * page load (last-visited prefetch, viewport observer, fallback batch) —
 * without this, each fires its own webhook call because TanStack's
 * dataUpdatedAt only lands AFTER the first call completes, so freshness
 * checks can't see an in-flight request. All callers within the memo window
 * share ONE network call.
 */
const bundleMemo = new Map<string, { promise: Promise<Map<string, MinimalLeadData[]> | null>; at: number }>();
const BUNDLE_MEMO_TTL_MS = 10 * 1000;

export async function fetchLeadsBundleFromN8N(
  userEmail: string
): Promise<Map<string, MinimalLeadData[]> | null> {
  const memo = bundleMemo.get(userEmail);
  if (memo && Date.now() - memo.at < BUNDLE_MEMO_TTL_MS) return memo.promise;

  const promise = (async () => {
    try {
      const response = await n8nService.getLeadsCards({
        campaign_id: '', // any id — n8n ignores it and returns all campaigns
        client_email: userEmail,
      });

      if (!response.success || !response.data) {
        console.warn('Failed to fetch leads bundle from N8N:', response.error);
        return null;
      }

      if (!isLeadsBundleResponse(response.data)) {
        // Contract changed (or a filter honored the campaign_id) — signal the
        // caller to use per-campaign fetches.
        return null;
      }

      const bundle = new Map<string, MinimalLeadData[]>();
      for (const campaign of response.data.campaigns) {
        if (!campaign?.campaign_id) continue;
        const leads = (campaign.leads || []).map((lead: any) =>
          applyPendingIsrWritesToCard(
            transformLeadCard({
              ...lead,
              campaign_id: lead.campaign_id || campaign.campaign_id,
              campaign_name: lead.campaign_name || campaign.campaign_name || '',
            }),
            campaign.campaign_id,
          )
        );
        bundle.set(campaign.campaign_id, leads.sort((a, b) => (b.icpScore || 0) - (a.icpScore || 0)));
      }
      return bundle;
    } catch (error) {
      console.error('Error fetching leads bundle from N8N:', error);
      // Release the memo slot on failure so the next caller retries fresh.
      bundleMemo.delete(userEmail);
      return null;
    }
  })();

  bundleMemo.set(userEmail, { promise, at: Date.now() });
  return promise;
}

/**
 * Fetch full lead data (for detail view with all tabs)
 * Returns transformed MappedLeadData directly from N8N (bypassing OptimalLead)
 */
export async function fetchFullLeadFromN8N(
  leadId: string,
  campaignId: string,
  userEmail: string
): Promise<MappedLeadData | null> {
  // THROWS on transient failures instead of resolving null. TanStack Query
  // only retries thrown errors — a null resolution counts as success, so a
  // momentary n8n/webhook hiccup used to surface as a permanent
  // "Lead workspace unavailable" screen with no retry. Letting the query
  // retry keeps consumers in their loading/pending branch until the fetch
  // either succeeds or genuinely exhausts its attempts.
  const response = await n8nService.getLead({
    lead_id: leadId,
    campaign_id: campaignId,
    client_email: userEmail,
  });

  if (!response.success || !response.data) {
    console.warn('Failed to fetch full lead from N8N:', response.error);
    throw new Error(response.error || 'Failed to fetch lead data from N8N');
  }

  // Check if lead data exists (N8N workflows might not be fully configured yet
  // — or the workflow is mid-write for this lead). Treat as transient so the
  // query retries at least once before the UI declares the lead unavailable.
  if (!response.data.lvl2 && !response.data.lvl1_person) {
    throw new Error(`No lead data in N8N response for lead ${leadId}`);
  }

  // Transform N8N lead data directly to MappedLeadData (bypasses OptimalLead)
  return transformN8NToMappedLead(response.data);
}

/**
 * Fetch all campaigns with metrics for a user.
 * Returns { campaigns, teamMembers } where teamMembers is populated by n8n-proxy
 * when the caller has team_admin or super_admin label.
 *
 * THROWS on failure (never returns an empty list on error): TanStack keeps the
 * last successful data cached on refetch error, so a transient n8n-proxy
 * failure can no longer wipe the user's campaign list into the "Welcome / no
 * campaigns" empty state. An empty list is ONLY returned when the proxy
 * definitively answered with zero campaigns.
 */
export async function fetchCampaignsFromN8N(
  userEmail: string
): Promise<CampaignsResult> {
  const response = await n8nService.getCampaignMetrics({
    client_email: userEmail,
  });

  if (!response.success || !response.data) {
    console.error('Failed to fetch campaigns from N8N:', response.error);
    throw new Error(response.error || `N8N campaign metrics failed for ${userEmail}`);
  }

  // Check if campaigns array exists (N8N workflows might not be fully configured yet).
  // A 2xx success response WITHOUT a campaigns array is a contract failure (or a
  // phantom/empty execution) — treat as an error, not as "user has no campaigns".
  if (!response.data.campaigns || !Array.isArray(response.data.campaigns)) {
    console.warn('No campaigns array in N8N response');
    throw new Error('N8N campaign metrics response missing campaigns array');
  }

  try {

    // Extract team_members from response (present when caller is team_admin/super_admin)
    const teamMembers: TeamMemberData[] = Array.isArray(response.data.team_members)
      ? response.data.team_members
      : [];
    const teamInvitations: TeamInvitationData[] = Array.isArray((response.data as any).team_invitations)
      ? (response.data as any).team_invitations
      : [];
    const isAdminContext = teamMembers.length > 0 || teamInvitations.length > 0;

    // Transform N8N campaigns to CampaignMetadata
    const campaigns = response.data.campaigns.map(transformCampaignMetrics);

    // Frontend email filter as defense-in-depth.
    // Skipped when n8n-proxy returned team_members (admin context) — in that case
    // n8n itself is responsible for returning only appropriate campaigns.
    if (isAdminContext) {
      return { campaigns, teamMembers, teamInvitations };
    }

    const currentUser = backendProvider.auth.getCurrentUser();
    const targetEmail = backendProvider.auth.getTargetEmail();
    const shouldLoadRosterFallback =
      Boolean(currentUser?.labels?.includes('teamadmin') || currentUser?.labels?.includes('superadmin')) ||
      Boolean(targetEmail && targetEmail !== currentUser?.email);

    if (shouldLoadRosterFallback) {
      try {
        const rosterResponse = await teamInviteService.getTeamRoster({
          admin_user_id: currentUser?.uid || undefined,
          admin_email: targetEmail || currentUser?.email || userEmail,
        });

        const fallbackTeamMembers: TeamMemberData[] = Array.isArray(rosterResponse.data?.team_members)
          ? rosterResponse.data!.team_members
          : [];
        const fallbackTeamInvitations: TeamInvitationData[] = Array.isArray(rosterResponse.data?.team_invitations)
          ? rosterResponse.data!.team_invitations
          : [];

        if (fallbackTeamMembers.length > 0 || fallbackTeamInvitations.length > 0) {
          return { campaigns, teamMembers: fallbackTeamMembers, teamInvitations: fallbackTeamInvitations };
        }
      } catch (error) {
        console.warn('⚠️ Failed to fetch Appwrite team roster fallback:', error);
      }
    }

    const userEmailLower = userEmail.toLowerCase().trim();
    const currentUserId = currentUser?.uid || null;
    const filteredCampaigns = campaigns.filter(campaign => {
      const campaignEmailLower = (campaign.client_email || '').toLowerCase().trim();
      return campaignEmailLower === userEmailLower
        || (currentUserId !== null && campaign.account_manager_id === currentUserId);
    });

    return { campaigns: filteredCampaigns, teamMembers: [], teamInvitations: [] };
  } catch (error) {
    // Roster fallback / transform errors land here. Re-throw so the query
    // errors and the cached campaign list survives (see function docblock).
    console.error('Error fetching campaigns from N8N:', error);
    throw error;
  }
}

/**
 * Fetch a single campaign by ID
 * Returns transformed CampaignMetadata, or null ONLY when the fetch succeeded
 * and the campaign genuinely isn't in the user's list. Transport failures
 * propagate — returning null on error made the campaign-metadata query cache
 * "deleted" for a campaign that merely failed to load once.
 */
export async function fetchCampaignByIdFromN8N(
  campaignId: string,
  userEmail: string
): Promise<CampaignMetadata | null> {
  // Reject the sentinel value the N8N lead transformer emits when lvl2.campaign_id
  // is missing — it can never match a real campaign and previously caused every
  // campaign-dependent view (e.g. ISR Briefing) to render empty.
  const trimmedId = campaignId?.trim();
  if (!trimmedId || trimmedId.toLowerCase() === 'unknown') {
    console.warn(`⚠️ fetchCampaignByIdFromN8N: invalid campaignId "${campaignId}"`);
    return null;
  }

  // Get all campaigns, then filter. fetchCampaignsFromN8N throws on transport
  // failure — exactly what we want here (see its docblock).
  const { campaigns } = await fetchCampaignsFromN8N(userEmail);

  // Tolerant match: exact first, then case-insensitive after trimming, so minor
  // format differences between the lead's campaign_id and the campaign's id don't
  // silently drop the whole campaign object.
  const normalizedId = trimmedId.toLowerCase();
  const match = campaigns.find(c => c.id === trimmedId)
    || campaigns.find(c => c.id && c.id.trim().toLowerCase() === normalizedId);

  if (!match) {
    console.warn(
      `⚠️ Campaign ${trimmedId} not found in ${campaigns.length} N8N campaigns`,
      campaigns.map(c => c.id).filter(Boolean)
    );
  }
  return match || null;
}
